// Need to use the React-specific entry point to import createApi
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logoutUser } from '../redux/account/accountSlice';
import { RootState } from '../redux/store';
import { API_URL } from '../Config/config';


export const baseQuery = fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers, { getState }) => {
        const { initialized, token, _id: accountId } = (getState() as RootState).account
        if (initialized && token) { headers.set('Authorization', `Bearer ${token}`) }
        if (initialized && accountId) { headers.set('bcuid', accountId) }
        if (!headers.has('Content-Type') && !headers.has('Disable-Content-Type')) {
            headers.set('Content-Type', 'application/json')
        }
        // headers.set('Content-Type', 'application/json')
        return headers
    }
})

// DOCS: https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#preventing-multiple-unauthorized-errors
const baseQueryWithAuthHandler: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (baseArgs, api, extraOptions) => {

    // if(typeof args === 'object' && args?.body?.propertiesQuery) {
    //     if (!args.body.propertiesQuery.skibidi){
    //         args.body.propertiesQuery.skibidi = 'skibidi'
    //     }
    // }

    if (typeof baseArgs === 'object' && baseArgs?.body?.pagination?.all) {
        const args = typeof baseArgs === 'string' ? baseArgs : JSON.parse(JSON.stringify(baseArgs))
        return getAllMongoPaginatedData(args, api, extraOptions)
    }

    let result = await baseQuery(baseArgs, api, extraOptions)
    if (result.error && result.error.status === 401) {
        api.dispatch(logoutUser())
        // // try to get a new token
        // const refreshResult = await baseQuery('/refreshToken', api, extraOptions)
        // if (refreshResult.data) {
        //   // store the new token
        //   api.dispatch(tokenReceived(refreshResult.data))
        //   // retry the initial query
        //   result = await baseQuery(args, api, extraOptions)
        // } else {
        //   api.dispatch(loggedOut())
        // }
    }

    return result
}

const getAllMongoPaginatedData = async (args: any, api: any, extraOptions: any) => {
    const baseQueryArgs = { ...args.body }
    let pagination = {
        ...baseQueryArgs.pagination,
        // sort: {}
    }
    delete baseQueryArgs.pagination
    const dataList: any[] = []
    let done = false
    let meta: any = {}
    try {
        while (!done) {
            const newArgs = {
                ...args,
                body: { ...baseQueryArgs, pagination: pagination }
            }
            const dataBatch: any = await baseQuery(newArgs, api, extraOptions)

            if (dataBatch.error && dataBatch.error.status === 401) {
                api.dispatch(logoutUser())
                return dataBatch
            }

            if (dataBatch.error) {
                return dataBatch
            }
            if (dataBatch.meta && Object.keys(meta).length === 0) {
                meta = dataBatch.meta
            }
            if (dataBatch.data?.docs) {
                dataList.push(...dataBatch.data.docs)
            }
            if (dataBatch.data?.nextPage) {
                // pagination.page = dataBatch.data.nextPage
                pagination = {
                    ...pagination,
                    page: dataBatch.data.nextPage
                }
                continue
            }
            done = true
        }
    } catch (err) {
        console.log('error', err)
        done = true
    }
    return {
        data: {
            docs: dataList,
            ...pagination,
            page: 1,
            limit: dataList.length,
            nextPage: null,
            prevPage: null,
            totalDocs: dataList.length,
            totalPages: 1,
        },
        meta
    }
}

// Define a service using a base URL and expected endpoints
export const bClientApi = createApi({
    reducerPath: 'bClientApi',
    baseQuery: baseQueryWithAuthHandler,
    refetchOnFocus: true,
    tagTypes: [
        'Property',
        'Booking',
        'BookingRequest',
        'BookingGuest',
        'Building',
        'Bukin',
        'Bukible',
        'Inquiry',
        'PropertyPrice',
        'Purchase',
        'PropertyTask',
        'BookingMessage',
        'MediaFile',
        'IcalData',
        'User',
        'DataLog',
        'NotificationEntry',
        'AdditionalService',
        'ResourceAccess',
        'ScrapeEntry',
        'KeyValue',
        'Turnover',
        'Amenity',
        'PropertyAmenity',
        'BuildingAmenity',
        'Integration',
        'VRBOProperty',
        'VRBOWebhook',
    ],
    endpoints: (builder) => ({

        // PROPERTIES /////////////////////////////////////////////
        getPropertiesList: builder.query<any, any>({
            query: (query: { propertiesQuery: any, pagination: PaginationType }) => ({
                url: `properties/find`,
                method: 'POST',
                body: query
            }),
            providesTags: ['Property'],
        }),
        addNewProperty: builder.mutation<any, any>({
            query: (propertyInfo) => ({
                url: 'properties/add-property',
                method: 'POST',
                body: { propertyInfo },
            }),
            invalidatesTags: ['Property'],
        }),
        editProperty: builder.mutation<any, { propertyId: string, propertyInfo: any }>({
            query: ({ propertyId, propertyInfo }) => ({
                url: 'properties/edit-property',
                method: 'POST',
                body: { _id: propertyId, propertyInfo },
            }),
            invalidatesTags: ['Property'],
        }),
        deleteProperty: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: 'properties/delete-property',
                method: 'POST',
                body: { _id: propertyId },
            }),
            invalidatesTags: ['Property'],
        }),
        importProperties: builder.mutation<any, any>({
            query: (properties) => ({
                url: 'properties/import-properties',
                method: 'POST',
                body: { properties },
            }),
            invalidatesTags: ['Property'],
        }),
        bulkEditProperties: builder.mutation<any, any>({
            query: (properties) => ({
                url: 'properties/bulk-edit-properties',
                method: 'POST',
                body: { properties },
            }),
            invalidatesTags: ['Property'],
        }),
        getPropertyById: builder.query<any, string>({
            query: (propertyId) => ({
                url: `properties/view/${propertyId}`,
                method: 'GET',
            }),
            providesTags: ['Property'],
        }),
        handleDoorLockState: builder.mutation<any, any>({
            query: (doorData: {
                doorId: string,
                action: string,
            }) => ({
                url: 'properties/remote-lock/handle-door-state',
                method: 'POST',
                body: doorData
            }),
        }),

        // BOOKINGS /////////////////////////////////////////////
        getBookings: builder.query<any, { bookingsQuery: any, pagination: PaginationType }>({
            query: ({ bookingsQuery, pagination }) => ({
                url: 'bookings/find',
                method: 'POST',
                body: { bookingsQuery, pagination }
            }),
            providesTags: ['Booking'],
        }),
        addNewBooking: builder.mutation<any, any>({
            query: (bookingInfo) => ({
                url: 'bookings/add-booking',
                method: 'POST',
                body: { bookingInfo },
            }),
            invalidatesTags: ['Booking', 'DataLog'],
        }),
        editBooking: builder.mutation<any, { bookingId: string, bookingInfo: any }>({
            query: ({ bookingId, bookingInfo }) => ({
                url: 'bookings/edit-booking',
                method: 'POST',
                body: { _id: bookingId, bookingInfo },
            }),
            invalidatesTags: ['Booking', 'DataLog'],
        }),
        deleteBooking: builder.mutation<any, string>({
            query: (bookingId) => ({
                url: 'bookings/delete-booking',
                method: 'POST',
                body: { _id: bookingId },
            }),
            invalidatesTags: ['Booking', 'DataLog'],
        }),
        importBookings: builder.mutation<any, any>({
            query: (bookings) => ({
                url: 'bookings/import-bookings',
                method: 'POST',
                body: { bookings },
            }),
            invalidatesTags: ['Booking', 'DataLog'],
        }),
        bulkEditBookings: builder.mutation<any, any>({
            query: (bookings) => ({
                url: 'bookings/bulk-edit-bookings',
                method: 'POST',
                body: { bookings },
            }),
            invalidatesTags: ['Booking', 'DataLog'],
        }),
        getBookingById: builder.query<any, string>({
            query: (bookingId) => ({
                url: `bookings/view/${bookingId}`,
                method: 'GET',
            }),
            providesTags: ['Booking'],
        }),

        // BOOKING REQUESTS /////////////////////////////////////////////
        getBookingRequests: builder.query<any, { bookingRequestsQuery: any, pagination: PaginationType }>({
            query: ({ bookingRequestsQuery, pagination }) => ({
                url: 'booking-requests/find',
                method: 'POST',
                body: { bookingRequestsQuery, pagination }
            }),
            providesTags: ['BookingRequest'],
        }),
        addNewBookingRequest: builder.mutation<any, any>({
            query: (bookingRequestInfo) => ({
                url: 'booking-requests/add-booking-request',
                method: 'POST',
                body: { bookingRequestInfo },
            }),
            invalidatesTags: ['BookingRequest'],
        }),
        editBookingRequest: builder.mutation<any, { bookingRequestId: string, bookingRequestInfo: any }>({
            query: ({ bookingRequestId, bookingRequestInfo }) => ({
                url: 'booking-requests/edit-booking-request',
                method: 'POST',
                body: { _id: bookingRequestId, bookingRequestInfo },
            }),
            invalidatesTags: ['BookingRequest'],
        }),
        deleteBookingRequest: builder.mutation<any, string>({
            query: (bookingRequestId) => ({
                url: 'booking-requests/delete-booking-request',
                method: 'POST',
                body: { _id: bookingRequestId },
            }),
            invalidatesTags: ['BookingRequest'],
        }),
        importBookingRequests: builder.mutation<any, any>({
            query: (bookingRequests) => ({
                url: 'booking-requests/import-booking-requests',
                method: 'POST',
                body: { bookingRequests },
            }),
            invalidatesTags: ['BookingRequest'],
        }),
        bulkEditBookingRequests: builder.mutation<any, any>({
            query: (bookingRequests) => ({
                url: 'booking-requests/bulk-edit-booking-requests',
                method: 'POST',
                body: { bookingRequests },
            }),
            invalidatesTags: ['BookingRequest'],
        }),
        getBookingRequestById: builder.query<any, string>({
            query: (bookingRequestId) => ({
                url: `booking-requests/view/${bookingRequestId}`,
                method: 'GET',
            }),
            providesTags: ['BookingRequest'],
        }),

        // BOOKING GUESTS /////////////////////////////////////////////
        getBookingGuests: builder.query<any, { bookingGuestsQuery: any, pagination: PaginationType }>({
            query: ({ bookingGuestsQuery, pagination }) => ({
                url: 'booking-guests/find',
                method: 'POST',
                body: { bookingGuestsQuery, pagination }
            }),
            providesTags: ['BookingGuest'],
        }),
        addNewBookingGuest: builder.mutation<any, any>({
            query: (bookingGuestInfo) => ({
                url: 'booking-guests/add-booking-guest',
                method: 'POST',
                body: { bookingGuestInfo },
            }),
            invalidatesTags: ['BookingGuest'],
        }),
        editBookingGuest: builder.mutation<any, { bookingGuestId: string, bookingGuestInfo: any }>({
            query: ({ bookingGuestId, bookingGuestInfo }) => ({
                url: 'booking-guests/edit-booking-guest',
                method: 'POST',
                body: { _id: bookingGuestId, bookingGuestInfo },
            }),
            invalidatesTags: ['BookingGuest'],
        }),
        deleteBookingGuest: builder.mutation<any, string>({
            query: (bookingGuestId) => ({
                url: 'booking-guests/delete-booking-guest',
                method: 'POST',
                body: { _id: bookingGuestId },
            }),
            invalidatesTags: ['BookingGuest'],
        }),
        importBookingGuests: builder.mutation<any, any>({
            query: (bookingGuests) => ({
                url: 'booking-guests/import-booking-guests',
                method: 'POST',
                body: { bookingGuests },
            }),
            invalidatesTags: ['BookingGuest'],
        }),
        bulkEditBookingGuests: builder.mutation<any, any>({
            query: (bookingGuests) => ({
                url: 'booking-guests/bulk-edit-booking-guests',
                method: 'POST',
                body: { bookingGuests },
            }),
            invalidatesTags: ['BookingGuest'],
        }),
        getBookingGuestById: builder.query<any, string>({
            query: (bookingGuestId) => ({
                url: `booking-guests/view/${bookingGuestId}`,
                method: 'GET',
            }),
            providesTags: ['BookingGuest'],
        }),
        sendBookingGuestEmail: builder.mutation<any, string>({
            query: (bookingGuestId) => ({
                url: `booking-guests/send-booking-guest-info/${bookingGuestId}`,
                method: 'GET',
            }),
            invalidatesTags: ['BookingGuest'],
        }),

        // BUILDINGS /////////////////////////////////////////////
        getBuildings: builder.query<any, { buildingsQuery: any, pagination: PaginationType }>({
            query: ({ buildingsQuery, pagination }) => ({
                url: 'buildings/find',
                method: 'POST',
                body: { buildingsQuery, pagination }
            }),
            providesTags: ['Building'],
        }),
        addNewBuilding: builder.mutation<any, any>({
            query: (buildingInfo) => ({
                url: 'buildings/add-building',
                method: 'POST',
                body: { buildingInfo },
            }),
            invalidatesTags: ['Building'],
        }),
        editBuilding: builder.mutation<any, { buildingId: string, buildingInfo: any }>({
            query: ({ buildingId, buildingInfo }) => ({
                url: 'buildings/edit-building',
                method: 'POST',
                body: { _id: buildingId, buildingInfo },
            }),
            invalidatesTags: ['Building'],
        }),
        deleteBuilding: builder.mutation<any, string>({
            query: (buildingId) => ({
                url: 'buildings/delete-building',
                method: 'POST',
                body: { _id: buildingId },
            }),
            invalidatesTags: ['Building'],
        }),
        importBuildings: builder.mutation<any, any>({
            query: (buildings) => ({
                url: 'buildings/import-buildings',
                method: 'POST',
                body: { buildings },
            }),
            invalidatesTags: ['Building'],
        }),
        bulkEditBuildings: builder.mutation<any, any>({
            query: (buildings) => ({
                url: 'buildings/bulk-edit-buildings',
                method: 'POST',
                body: { buildings },
            }),
            invalidatesTags: ['Building'],
        }),
        getBuildingById: builder.query<any, string>({
            query: (buildingId) => ({
                url: `buildings/view/${buildingId}`,
                method: 'GET',
            }),
            providesTags: ['Building'],
        }),

        // BUKINS /////////////////////////////////////////////
        getBukins: builder.query<any, { bukinsQuery: any, pagination: PaginationType }>({
            query: ({ bukinsQuery, pagination }) => ({
                url: 'bukins/find',
                method: 'POST',
                body: { bukinsQuery, pagination }
            }),
            providesTags: ['Bukin'],
        }),
        addNewBukin: builder.mutation<any, any>({
            query: (bukinInfo) => ({
                url: 'bukins/add-bukin',
                method: 'POST',
                body: { bukinInfo },
            }),
            invalidatesTags: ['Bukin'],
        }),
        editBukin: builder.mutation<any, { bukinId: string, bukinInfo: any }>({
            query: ({ bukinId, bukinInfo }) => ({
                url: 'bukins/edit-bukin',
                method: 'POST',
                body: { _id: bukinId, bukinInfo },
            }),
            invalidatesTags: ['Bukin'],
        }),
        deleteBukin: builder.mutation<any, string>({
            query: (bukinId) => ({
                url: 'bukins/delete-bukin',
                method: 'POST',
                body: { _id: bukinId },
            }),
            invalidatesTags: ['Bukin'],
        }),
        importBukins: builder.mutation<any, any>({
            query: (bukins) => ({
                url: 'bukins/import-bukins',
                method: 'POST',
                body: { bukins },
            }),
            invalidatesTags: ['Bukin'],
        }),
        bulkEditBukins: builder.mutation<any, any>({
            query: (bukins) => ({
                url: 'bukins/bulk-edit-bukins',
                method: 'POST',
                body: { bukins },
            }),
            invalidatesTags: ['Bukin'],
        }),
        getBukinById: builder.query<any, string>({
            query: (bukinId) => ({
                url: `bukins/view/${bukinId}`,
                method: 'GET',
            }),
            providesTags: ['Bukin'],
        }),

        // BUKIBLES /////////////////////////////////////////////
        getBukibles: builder.query<any, { bukiblesQuery: any, pagination: PaginationType }>({
            query: ({ bukiblesQuery, pagination }) => ({
                url: 'bukibles/find',
                method: 'POST',
                body: { bukiblesQuery, pagination }
            }),
            providesTags: ['Bukible'],
        }),
        addNewBukible: builder.mutation<any, any>({
            query: (bukibleInfo) => ({
                url: 'bukibles/add-bukible',
                method: 'POST',
                body: { bukibleInfo },
            }),
            invalidatesTags: ['Bukible'],
        }),
        editBukible: builder.mutation<any, { bukibleId: string, bukibleInfo: any }>({
            query: ({ bukibleId, bukibleInfo }) => ({
                url: 'bukibles/edit-bukible',
                method: 'POST',
                body: { _id: bukibleId, bukibleInfo },
            }),
            invalidatesTags: ['Bukible'],
        }),
        deleteBukible: builder.mutation<any, string>({
            query: (bukibleId) => ({
                url: 'bukibles/delete-bukible',
                method: 'POST',
                body: { _id: bukibleId },
            }),
            invalidatesTags: ['Bukible'],
        }),
        importBukibles: builder.mutation<any, any>({
            query: (bukibles) => ({
                url: 'bukibles/import-bukibles',
                method: 'POST',
                body: { bukibles },
            }),
            invalidatesTags: ['Bukible'],
        }),
        bulkEditBukibles: builder.mutation<any, any>({
            query: (bukibles) => ({
                url: 'bukibles/bulk-edit-bukibles',
                method: 'POST',
                body: { bukibles },
            }),
            invalidatesTags: ['Bukible'],
        }),
        getBukibleById: builder.query<any, string>({
            query: (bukibleId) => ({
                url: `bukibles/view/${bukibleId}`,
                method: 'GET',
            }),
            providesTags: ['Bukible'],
        }),

        // INQUIRIES /////////////////////////////////////////////
        getInquiries: builder.query<any, { inquiriesQuery: any, pagination: PaginationType }>({
            query: ({ inquiriesQuery, pagination }) => ({
                url: 'inquiries/find',
                method: 'POST',
                body: { inquiriesQuery, pagination }
            }),
            providesTags: ['Inquiry'],
        }),
        addNewInquiry: builder.mutation<any, any>({
            query: (inquiryInfo) => ({
                url: 'inquiries/add-inquiry',
                method: 'POST',
                body: { inquiryInfo },
            }),
            invalidatesTags: ['Inquiry'],
        }),
        editInquiry: builder.mutation<any, { inquiryId: string, inquiryInfo: any }>({
            query: ({ inquiryId, inquiryInfo }) => ({
                url: 'inquiries/edit-inquiry',
                method: 'POST',
                body: { _id: inquiryId, inquiryInfo },
            }),
            invalidatesTags: ['Inquiry'],
        }),
        deleteInquiry: builder.mutation<any, string>({
            query: (inquiryId) => ({
                url: 'inquiries/delete-inquiry',
                method: 'POST',
                body: { _id: inquiryId },
            }),
            invalidatesTags: ['Inquiry'],
        }),
        importInquiries: builder.mutation<any, any>({
            query: (inquiries) => ({
                url: 'inquiries/import-inquiries',
                method: 'POST',
                body: { inquiries },
            }),
            invalidatesTags: ['Inquiry'],
        }),
        bulkEditInquiries: builder.mutation<any, any>({
            query: (inquiries) => ({
                url: 'inquiries/bulk-edit-inquiries',
                method: 'POST',
                body: { inquiries },
            }),
            invalidatesTags: ['Inquiry'],
        }),
        getInquiryById: builder.query<any, string>({
            query: (inquiryId) => ({
                url: `inquiries/view/${inquiryId}`,
                method: 'GET',
            }),
            providesTags: ['Inquiry'],
        }),

        // PROPERTY PRICES /////////////////////////////////////////////
        getPropertyPrices: builder.query<any, { propertyPricesQuery: any, pagination: PaginationType }>({
            query: ({ propertyPricesQuery, pagination }) => ({
                url: 'property-prices/find',
                method: 'POST',
                body: { propertyPricesQuery, pagination }
            }),
            providesTags: ['PropertyPrice'],
        }),
        addNewPropertyPrice: builder.mutation<any, any>({
            query: (propertyPriceInfo) => ({
                url: 'property-prices/add-property-price',
                method: 'POST',
                body: { propertyPriceInfo },
            }),
            invalidatesTags: ['PropertyPrice'],
        }),
        editPropertyPrice: builder.mutation<any, { propertyPriceId: string, propertyPriceInfo: any }>({
            query: ({ propertyPriceId, propertyPriceInfo }) => ({
                url: 'property-prices/edit-property-price',
                method: 'POST',
                body: { _id: propertyPriceId, propertyPriceInfo },
            }),
            invalidatesTags: ['PropertyPrice'],
        }),
        deletePropertyPrice: builder.mutation<any, string>({
            query: (propertyPriceId) => ({
                url: 'property-prices/delete-property-price',
                method: 'POST',
                body: { _id: propertyPriceId },
            }),
            invalidatesTags: ['PropertyPrice'],
        }),
        importPropertyPrices: builder.mutation<any, any>({
            query: (propertyPrices) => ({
                url: 'property-prices/import-property-prices',
                method: 'POST',
                body: { propertyPrices },
            }),
            invalidatesTags: ['PropertyPrice'],
        }),
        bulkEditPropertyPrices: builder.mutation<any, any>({
            query: (propertyPrices) => ({
                url: 'property-prices/bulk-edit-property-prices',
                method: 'POST',
                body: { propertyPrices },
            }),
            invalidatesTags: ['PropertyPrice'],
        }),
        getPropertyPriceById: builder.query<any, string>({
            query: (propertyPriceId) => ({
                url: `property-prices/view/${propertyPriceId}`,
                method: 'GET',
            }),
            providesTags: ['PropertyPrice'],
        }),

        // PURCHASES /////////////////////////////////////////////
        getPurchases: builder.query<any, { purchasesQuery: any, pagination: PaginationType }>({
            query: ({ purchasesQuery, pagination }) => ({
                url: 'purchases/find',
                method: 'POST',
                body: { purchasesQuery, pagination }
            }),
            providesTags: ['Purchase'],
        }),
        addNewPurchase: builder.mutation<any, any>({
            query: (purchaseInfo) => ({
                url: 'purchases/add-purchase',
                method: 'POST',
                body: { purchaseInfo },
            }),
            invalidatesTags: ['Purchase'],
        }),
        editPurchase: builder.mutation<any, { purchaseId: string, purchaseInfo: any }>({
            query: ({ purchaseId, purchaseInfo }) => ({
                url: 'purchases/edit-purchase',
                method: 'POST',
                body: { _id: purchaseId, purchaseInfo },
            }),
            invalidatesTags: ['Purchase'],
        }),
        deletePurchase: builder.mutation<any, string>({
            query: (purchaseId) => ({
                url: 'purchases/delete-purchase',
                method: 'POST',
                body: { _id: purchaseId },
            }),
            invalidatesTags: ['Purchase'],
        }),
        importPurchases: builder.mutation<any, any>({
            query: (purchases) => ({
                url: 'purchases/import-purchases',
                method: 'POST',
                body: { purchases },
            }),
            invalidatesTags: ['Purchase'],
        }),
        bulkEditPurchases: builder.mutation<any, any>({
            query: (purchases) => ({
                url: 'purchases/bulk-edit-purchases',
                method: 'POST',
                body: { purchases },
            }),
            invalidatesTags: ['Purchase'],
        }),
        getPurchaseById: builder.query<any, string>({
            query: (purchaseId) => ({
                url: `purchases/view/${purchaseId}`,
                method: 'GET',
            }),
            providesTags: ['Purchase'],
        }),

        // PROPERTY TASKS /////////////////////////////////////////////
        getPropertyTasks: builder.query<any, { propertyTasksQuery: any, pagination: PaginationType }>({
            query: ({ propertyTasksQuery, pagination }) => ({
                url: 'property-tasks/find',
                method: 'POST',
                body: { propertyTasksQuery, pagination }
            }),
            providesTags: ['PropertyTask'],
        }),
        addNewPropertyTask: builder.mutation<any, any>({
            query: (propertyTaskInfo) => ({
                url: 'property-tasks/add-property-task',
                method: 'POST',
                body: { propertyTaskInfo },
            }),
            invalidatesTags: ['PropertyTask'],
        }),
        editPropertyTask: builder.mutation<any, { propertyTaskId: string, propertyTaskInfo: any }>({
            query: ({ propertyTaskId, propertyTaskInfo }) => ({
                url: 'property-tasks/edit-property-task',
                method: 'POST',
                body: { _id: propertyTaskId, propertyTaskInfo },
            }),
            invalidatesTags: ['PropertyTask'],
        }),
        deletePropertyTask: builder.mutation<any, string>({
            query: (propertyTaskId) => ({
                url: 'property-tasks/delete-property-task',
                method: 'POST',
                body: { _id: propertyTaskId },
            }),
            invalidatesTags: ['PropertyTask'],
        }),
        getPropertyTaskById: builder.query<any, string>({
            query: (propertyTaskId) => ({
                url: `property-tasks/view/${propertyTaskId}`,
                method: 'GET',
            }),
            providesTags: ['PropertyTask'],
        }),

        // BOOKING MESSAGES /////////////////////////////////////////////
        getBookingMessages: builder.query<any, { bookingMessagesQuery: any, pagination: PaginationType }>({
            query: ({ bookingMessagesQuery, pagination }) => ({
                url: 'booking-messages/find',
                method: 'POST',
                body: { bookingMessagesQuery, pagination }
            }),
            providesTags: ['BookingMessage'],
        }),
        replyToBookingMessage: builder.mutation<any, { bookingMessageInfo: any, replyEmail: string }>({
            query: ({ bookingMessageInfo, replyEmail }) => ({
                url: 'booking-messages/reply-booking-message',
                method: 'POST',
                body: { bookingMessageInfo, replyEmail },
            }),
            invalidatesTags: ['BookingMessage'],
        }),

        // MEDIA FILES /////////////////////////////////////////////
        getMediaFiles: builder.query<any, { mediaFilesQuery: any, pagination: PaginationType }>({
            query: ({ mediaFilesQuery, pagination }) => ({
                url: 'mediaFiles/find',
                method: 'POST',
                body: { mediaFilesQuery, pagination }
            }),
            providesTags: ['MediaFile'],
        }),
        addNewMediaFile: builder.mutation<any, any>({
            query: (mediaFileInfo) => ({
                url: 'mediaFiles/add-mediaFile',
                method: 'POST',
                body: { mediaFileInfo },
            }),
            invalidatesTags: ['MediaFile'],
        }),
        editMediaFile: builder.mutation<any, { mediaFileId: string, mediaFileInfo: any }>({
            query: ({ mediaFileId, mediaFileInfo }) => ({
                url: 'mediaFiles/edit-mediaFile',
                method: 'POST',
                body: { _id: mediaFileId, mediaFileInfo },
            }),
            invalidatesTags: ['MediaFile'],
        }),
        deleteMediaFile: builder.mutation<any, string>({
            query: (mediaFileId) => ({
                url: 'mediaFiles/delete-mediaFile',
                method: 'POST',
                body: { _id: mediaFileId },
            }),
            invalidatesTags: ['MediaFile'],
        }),
        importMediaFiles: builder.mutation<any, any>({
            query: (mediaFiles) => ({
                url: 'mediaFiles/import-mediaFiles',
                method: 'POST',
                body: { mediaFiles },
            }),
            invalidatesTags: ['MediaFile'],
        }),
        bulkEditMediaFiles: builder.mutation<any, any>({
            query: (mediaFiles) => ({
                url: 'mediaFiles/bulk-edit-mediaFiles',
                method: 'POST',
                body: { mediaFiles },
            }),
            invalidatesTags: ['MediaFile'],
        }),
        getMediaFileById: builder.query<any, string>({
            query: (mediaFileId) => ({
                url: `mediaFiles/view/${mediaFileId}`,
                method: 'GET',
            }),
            providesTags: ['MediaFile'],
        }),

        // ICAL RELATED CALLS /////////////////////////////////////////////
        getPropertyIcalData: builder.query<any, string>({
            query: (propertyId) => ({
                url: `properties/ical-data/${propertyId}`,
                method: 'GET',
            }),
            providesTags: ['IcalData'],
        }),



        // DATALOGS /////////////////////////////////////////////
        getDataLogs: builder.query<any, { dataLogsQuery: any, pagination: PaginationType }>({
            query: ({ dataLogsQuery, pagination }) => ({
                url: 'data-logs/find',
                method: 'POST',
                body: { dataLogsQuery, pagination }
            }),
            providesTags: ['DataLog'],
        }),
        addNewDataLog: builder.mutation<any, any>({
            query: (dataLogInfo) => ({
                url: 'data-logs/add-data-log',
                method: 'POST',
                body: { dataLogInfo },
            }),
            invalidatesTags: ['DataLog'],
        }),
        editDataLog: builder.mutation<any, { dataLogId: string, dataLogInfo: any }>({
            query: ({ dataLogId, dataLogInfo }) => ({
                url: 'data-logs/edit-data-log',
                method: 'POST',
                body: { _id: dataLogId, dataLogInfo },
            }),
            invalidatesTags: ['DataLog'],
        }),
        deleteDataLog: builder.mutation<any, string>({
            query: (dataLogId) => ({
                url: 'data-logs/delete-data-log',
                method: 'POST',
                body: { _id: dataLogId },
            }),
            invalidatesTags: ['DataLog'],
        }),
        importDataLogs: builder.mutation<any, any>({
            query: (dataLogs) => ({
                url: 'data-logs/import-data-logs',
                method: 'POST',
                body: { dataLogs },
            }),
            invalidatesTags: ['DataLog'],
        }),
        bulkEditDataLogs: builder.mutation<any, any>({
            query: (dataLogs) => ({
                url: 'data-logs/bulk-edit-data-logs',
                method: 'POST',
                body: { dataLogs },
            }),
            invalidatesTags: ['DataLog'],
        }),
        getDataLogById: builder.query<any, string>({
            query: (dataLogId) => ({
                url: `data-logs/view/${dataLogId}`,
                method: 'GET',
            }),
            providesTags: ['DataLog'],
        }),

        // NOTIFICATION ENTRY /////////////////////////////////////////////
        getNotificationEntries: builder.query<any, { notificationEntriesQuery: any, pagination: PaginationType }>({
            query: ({ notificationEntriesQuery, pagination }) => ({
                url: 'notification-entries/find',
                method: 'POST',
                body: { notificationEntriesQuery, pagination }
            }),
            providesTags: ['NotificationEntry'],
        }),
        addNewNotificationEntry: builder.mutation<any, any>({
            query: (notificationEntryInfo) => ({
                url: 'notification-entries/add-notification-entry',
                method: 'POST',
                body: { notificationEntryInfo },
            }),
            invalidatesTags: ['NotificationEntry'],
        }),
        editNotificationEntry: builder.mutation<any, { notificationEntryId: string, notificationEntryInfo: any }>({
            query: ({ notificationEntryId, notificationEntryInfo }) => ({
                url: 'notification-entries/edit-notification-entry',
                method: 'POST',
                body: { _id: notificationEntryId, notificationEntryInfo },
            }),
            invalidatesTags: ['NotificationEntry'],
        }),
        deleteNotificationEntry: builder.mutation<any, string>({
            query: (notificationEntryId) => ({
                url: 'notification-entries/delete-notification-entry',
                method: 'POST',
                body: { _id: notificationEntryId },
            }),
            invalidatesTags: ['NotificationEntry'],
        }),
        importNotificationEntries: builder.mutation<any, any>({
            query: (notificationEntries) => ({
                url: 'notification-entries/import-notification-entries',
                method: 'POST',
                body: { notificationEntries },
            }),
            invalidatesTags: ['NotificationEntry'],
        }),
        bulkEditNotificationEntries: builder.mutation<any, any>({
            query: (notificationEntries) => ({
                url: 'notification-entries/bulk-edit-notification-entries',
                method: 'POST',
                body: { notificationEntries },
            }),
            invalidatesTags: ['NotificationEntry'],
        }),
        getNotificationEntryById: builder.query<any, string>({
            query: (notificationEntryId) => ({
                url: `notification-entries/view/${notificationEntryId}`,
                method: 'GET',
            }),
            providesTags: ['NotificationEntry'],
        }),

        // ADDITIONAL SERVICE /////////////////////////////////////////////
        getAdditionalServices: builder.query<any, { additionalServicesQuery: any, pagination: PaginationType }>({
            query: ({ additionalServicesQuery, pagination }) => ({
                url: 'additional-services/find',
                method: 'POST',
                body: { additionalServicesQuery, pagination }
            }),
            providesTags: ['AdditionalService'],
        }),
        getAdditionalServiceById: builder.query<any, string>({
            query: (additionalServiceId) => ({
                url: `additional-services/view/${additionalServiceId}`,
                method: 'GET',
            }),
            providesTags: ['AdditionalService'],
        }),
        addNewAdditionalService: builder.mutation<any, any>({
            query: (additionalServiceInfo) => ({
                url: 'additional-services/add-additional-service',
                method: 'POST',
                body: { additionalServiceInfo },
            }),
            invalidatesTags: ['AdditionalService'],
        }),
        editAdditionalService: builder.mutation<any, { additionalServiceId: string, additionalServiceInfo: any }>({
            query: ({ additionalServiceId, additionalServiceInfo }) => ({
                url: 'additional-services/edit-additional-service',
                method: 'POST',
                body: { _id: additionalServiceId, additionalServiceInfo },
            }),
            invalidatesTags: ['AdditionalService'],
        }),
        deleteAdditionalService: builder.mutation<any, string>({
            query: (additionalServiceId) => ({
                url: 'additional-services/delete-additional-service',
                method: 'POST',
                body: { _id: additionalServiceId },
            }),
            invalidatesTags: ['AdditionalService'],
        }),
        importAdditionalServices: builder.mutation<any, any>({
            query: (additionalServices) => ({
                url: 'additional-services/import-additional-services',
                method: 'POST',
                body: { additionalServices },
            }),
            invalidatesTags: ['AdditionalService'],
        }),
        bulkEditAdditionalServices: builder.mutation<any, any>({
            query: (additionalServices) => ({
                url: 'additional-services/bulk-edit-additional-services',
                method: 'POST',
                body: { additionalServices },
            }),
            invalidatesTags: ['AdditionalService'],
        }),
        getAdditionalServiceDistinctFieldValues: builder.query<any, string>({
            query: (field) => ({
                url: `additional-services/distinct-field-values/${field}`,
                method: 'GET',
            }),
            providesTags: ['AdditionalService'],
        }),

        // TURNOVERS /////////////////////////////////////////////
        getTurnovers: builder.query<any, { turnoversQuery: any, pagination: PaginationType }>({
            query: ({ turnoversQuery, pagination }) => ({
                url: 'turnovers/find',
                method: 'POST',
                body: { turnoversQuery, pagination }
            }),
            providesTags: ['Turnover'],
        }),
        getTurnoverById: builder.query<any, string>({
            query: (turnoverId) => ({
                url: `turnovers/view/${turnoverId}`,
                method: 'GET',
            }),
            providesTags: ['Turnover'],
        }),
        addNewTurnover: builder.mutation<any, any>({
            query: (turnoverInfo) => ({
                url: 'turnovers/add-turnover',
                method: 'POST',
                body: { turnoverInfo },
            }),
            invalidatesTags: ['Turnover'],
        }),
        editTurnover: builder.mutation<any, { turnoverId: string, turnoverInfo: any }>({
            query: ({ turnoverId, turnoverInfo }) => ({
                url: 'turnovers/edit-turnover',
                method: 'POST',
                body: { _id: turnoverId, turnoverInfo },
            }),
            invalidatesTags: ['Turnover'],
        }),
        deleteTurnover: builder.mutation<any, string>({
            query: (turnoverId) => ({
                url: 'turnovers/delete-turnover',
                method: 'POST',
                body: { _id: turnoverId },
            }),
            invalidatesTags: ['Turnover'],
        }),
        importTurnovers: builder.mutation<any, any>({
            query: (turnovers) => ({
                url: 'turnovers/import-turnovers',
                method: 'POST',
                body: { turnovers },
            }),
            invalidatesTags: ['Turnover'],
        }),
        bulkEditTurnovers: builder.mutation<any, any>({
            query: (turnovers) => ({
                url: 'turnovers/bulk-edit-turnovers',
                method: 'POST',
                body: { turnovers },
            }),
            invalidatesTags: ['Turnover'],
        }),

        // RESOURCE ACCESS /////////////////////////////////////////////
        getResourceAccessList: builder.query<any, { resourceAccessQuery: any, pagination: PaginationType }>({
            query: (query: { resourceAccessQuery: any, pagination: PaginationType }) => ({
                url: `resource-access/find`,
                method: 'POST',
                body: query
            }),
            providesTags: ['ResourceAccess'],
        }),
        getResourceAccessById: builder.query<any, string>({
            query: (resourceAccessId) => ({
                url: `resource-access/view/${resourceAccessId}`,
                method: 'GET',
            }),
            providesTags: ['ResourceAccess'],
        }),
        addResourceAccess: builder.mutation<any, any>({
            query: (resourceAccessInfo) => ({
                url: 'resource-access/add-resource-access',
                method: 'POST',
                body: { resourceAccessInfo },
            }),
            invalidatesTags: ['ResourceAccess'],
        }),
        editResourceAccess: builder.mutation<any, { resourceAccessId: string, resourceAccessInfo: any }>({
            query: ({ resourceAccessId, resourceAccessInfo }) => ({
                url: 'resource-access/edit-resource-access',
                method: 'POST',
                body: { _id: resourceAccessId, resourceAccessInfo },
            }),
            invalidatesTags: ['ResourceAccess'],
        }),
        deleteResourceAccess: builder.mutation<any, string>({
            query: (resourceAccessId) => ({
                url: 'resource-access/delete-resource-access',
                method: 'POST',
                body: { _id: resourceAccessId },
            }),
            invalidatesTags: ['ResourceAccess'],
        }),
        importResourceAccess: builder.mutation<any, any>({
            query: (resourceAccesses) => ({
                url: 'resource-access/import-resource-accesses',
                method: 'POST',
                body: { resourceAccesses },
            }),
            invalidatesTags: ['ResourceAccess'],
        }),
        bulkEditResourceAccess: builder.mutation<any, any>({
            query: (resourceAccesses) => ({
                url: 'resource-access/bulk-edit-resource-accesses',
                method: 'POST',
                body: { resourceAccesses },
            }),
            invalidatesTags: ['ResourceAccess'],
        }),
        getResourceAccessDistinctFieldValues: builder.query<any, string>({
            query: (field) => ({
                url: `resource-access/distinct-field-values/${field}`,
                method: 'GET',
            }),
            providesTags: ['ResourceAccess'],
        }),
        getUserResourceAccess: builder.mutation<any, { userId: string, resourceId: string, dataType: string }>({
            query: ({ userId, resourceId, dataType }) => ({
                url: 'resource-access/user-resource-access',
                method: 'POST',
                body: { userId, resourceId, dataType },
            }),
            invalidatesTags: ['ResourceAccess'],
        }),
        getAllUserResourceAccess: builder.query<any, { userId: string, pagination?: PaginationType }>({
            query: ({ userId, pagination }) => ({
                url: `resource-access/all-user-resource-access/${userId}`,
                method: 'GET',
                params: pagination ? { pagination: JSON.stringify(pagination) } : undefined
            }),
            providesTags: ['ResourceAccess'],
        }),
        getAllResourceUsers: builder.mutation<any, { resourceId: string, dataType: string, pagination?: PaginationType }>({
            query: ({ resourceId, dataType, pagination }) => ({
                url: 'resource-access/all-resource-users',
                method: 'POST',
                body: { resourceId, dataType, pagination },
            }),
            invalidatesTags: ['ResourceAccess'],
        }),

        getUserResourceAccessProperties: builder.query<any, { userId: string | string[], pagination: PaginationType, allResourceData: boolean }>({
            query: (query: { userId: string, pagination: PaginationType, allResourceData: boolean }) => ({
                url: `resource-access/get-user-properties`,
                method: 'POST',
                body: query
            }),
            providesTags: ['ResourceAccess', 'Property'],
        }),

        // SCRAPE ENTRY /////////////////////////////////////////////
        getScrapeEntries: builder.query<any, { scrapeEntriesQuery: any, pagination: PaginationType }>({
            query: ({ scrapeEntriesQuery, pagination }) => ({
                url: 'scrape-entries/find',
                method: 'POST',
                body: { scrapeEntriesQuery, pagination },
            }),
            providesTags: ['ScrapeEntry'],
        }),
        getScrapeEntryById: builder.query<any, string>({
            query: (scrapeEntryId) => ({
                url: `scrape-entries/view/${scrapeEntryId}`,
                method: 'GET',
            }),
        }),
        addNewScrapeEntry: builder.mutation<any, any>({
            query: (scrapeEntryInfo) => ({
                url: 'scrape-entries/add-scrape-entry',
                method: 'POST',
                body: { scrapeEntryInfo },
            }),
            invalidatesTags: ['ScrapeEntry'],
        }),
        editScrapeEntry: builder.mutation<any, { scrapeEntryId: string, scrapeEntryInfo: any }>({
            query: ({ scrapeEntryId, scrapeEntryInfo }) => ({
                url: 'scrape-entries/edit-scrape-entry',
                method: 'POST',
                body: { _id: scrapeEntryId, scrapeEntryInfo },
            }),
            invalidatesTags: ['ScrapeEntry'],
        }),
        deleteScrapeEntry: builder.mutation<any, string>({
            query: (scrapeEntryId) => ({
                url: 'scrape-entries/delete-scrape-entry',
                method: 'POST',
                body: { _id: scrapeEntryId },
            }),
            invalidatesTags: ['ScrapeEntry'],
        }),

        // AMENITIES /////////////////////////////////////////////
        getAmenities: builder.query<any, { amenitiesQuery: any, pagination: PaginationType }>({
            query: ({ amenitiesQuery, pagination }) => ({
                url: 'amenities/find',
                method: 'POST',
                body: { amenitiesQuery, pagination }
            }),
            providesTags: ['Amenity'],
        }),
        getAmenityById: builder.query<any, string>({
            query: (amenityId) => ({
                url: `amenities/view/${amenityId}`,
                method: 'GET',
            }),
            providesTags: ['Amenity'],
        }),
        addNewAmenity: builder.mutation<any, any>({
            query: (amenityInfo) => ({
                url: 'amenities/add-amenity',
                method: 'POST',
                body: { amenityInfo },
            }),
            invalidatesTags: ['Amenity'],
        }),
        editAmenity: builder.mutation<any, { amenityId: string, amenityInfo: any }>({
            query: ({ amenityId, amenityInfo }) => ({
                url: 'amenities/edit-amenity',
                method: 'POST',
                body: { _id: amenityId, amenityInfo },
            }),
            invalidatesTags: ['Amenity'],
        }),
        deleteAmenity: builder.mutation<any, string>({
            query: (amenityId) => ({
                url: 'amenities/delete-amenity',
                method: 'POST',
                body: { _id: amenityId },
            }),
            invalidatesTags: ['Amenity'],
        }),

        // PROPERTY AMENITIES /////////////////////////////////////////////
        getPropertyAmenities: builder.query<any, { propertyAmenitiesQuery: any, pagination: PaginationType }>({
            query: ({ propertyAmenitiesQuery, pagination }) => ({
                url: 'property-amenities/find',
                method: 'POST',
                body: { propertyAmenitiesQuery, pagination }
            }),
            providesTags: ['PropertyAmenity'],
        }),
        getPropertyAmenityById: builder.query<any, string>({
            query: (propertyAmenityId) => ({
                url: `property-amenities/view/${propertyAmenityId}`,
                method: 'GET',
            }),
            providesTags: ['PropertyAmenity'],
        }),
        addNewPropertyAmenity: builder.mutation<any, any>({
            query: (propertyAmenityInfo) => ({
                url: 'property-amenities/add-property-amenity',
                method: 'POST',
                body: { propertyAmenityInfo },
            }),
            invalidatesTags: ['PropertyAmenity'],
        }),
        editPropertyAmenity: builder.mutation<any, { propertyAmenityId: string, propertyAmenityInfo: any }>({
            query: ({ propertyAmenityId, propertyAmenityInfo }) => ({
                url: 'property-amenities/edit-property-amenity',
                method: 'POST',
                body: { _id: propertyAmenityId, propertyAmenityInfo },
            }),
            invalidatesTags: ['PropertyAmenity'],
        }),
        deletePropertyAmenity: builder.mutation<any, string>({
            query: (propertyAmenityId) => ({
                url: 'property-amenities/delete-property-amenity',
                method: 'POST',
                body: { _id: propertyAmenityId },
            }),
            invalidatesTags: ['PropertyAmenity'],
        }),

        // BUILDING AMENITIES /////////////////////////////////////////////
        getBuildingAmenities: builder.query<any, { buildingAmenitiesQuery: any, pagination: PaginationType }>({
            query: ({ buildingAmenitiesQuery, pagination }) => ({
                url: 'building-amenities/find',
                method: 'POST',
                body: { buildingAmenitiesQuery, pagination }
            }),
            providesTags: ['BuildingAmenity'],
        }),
        getBuildingAmenityById: builder.query<any, string>({
            query: (buildingAmenityId) => ({
                url: `building-amenities/view/${buildingAmenityId}`,
                method: 'GET',
            }),
            providesTags: ['BuildingAmenity'],
        }),
        addNewBuildingAmenity: builder.mutation<any, any>({
            query: (buildingAmenityInfo) => ({
                url: 'building-amenities/add-building-amenity',
                method: 'POST',
                body: { buildingAmenityInfo },
            }),
            invalidatesTags: ['BuildingAmenity'],
        }),
        editBuildingAmenity: builder.mutation<any, { buildingAmenityId: string, buildingAmenityInfo: any }>({
            query: ({ buildingAmenityId, buildingAmenityInfo }) => ({
                url: 'building-amenities/edit-building-amenity',
                method: 'POST',
                body: { _id: buildingAmenityId, buildingAmenityInfo },
            }),
            invalidatesTags: ['BuildingAmenity'],
        }),
        deleteBuildingAmenity: builder.mutation<any, string>({
            query: (buildingAmenityId) => ({
                url: 'building-amenities/delete-building-amenity',
                method: 'POST',
                body: { _id: buildingAmenityId },
            }),
            invalidatesTags: ['BuildingAmenity'],
        }),

        // KEY VALUE /////////////////////////////////////////////
        getKeyValues: builder.query<any, { keyValuesQuery: any, pagination: PaginationType }>({
            query: ({ keyValuesQuery, pagination }) => ({
                url: 'key-values/find',
                method: 'POST',
                body: { keyValuesQuery, pagination },
            }),
            providesTags: ['KeyValue'],
        }),
        getKeyValueById: builder.query<any, string>({
            query: (keyValueId) => ({
                url: `key-values/view/${keyValueId}`,
                method: 'GET',
            }),
        }),
        addNewKeyValue: builder.mutation<any, any>({
            query: (keyValueInfo) => ({
                url: 'key-values/add-key-value',
                method: 'POST',
                body: { keyValueInfo },
            }),
            invalidatesTags: ['KeyValue'],
        }),
        editKeyValue: builder.mutation<any, { keyValueId: string, keyValueInfo: any }>({
            query: ({ keyValueId, keyValueInfo }) => ({
                url: 'key-values/edit-key-value',
                method: 'POST',
                body: { _id: keyValueId, keyValueInfo },
            }),
            invalidatesTags: ['KeyValue'],
        }),
        deleteKeyValue: builder.mutation<any, string>({
            query: (keyValueId) => ({
                url: 'key-values/delete-key-value',
                method: 'POST',
                body: { _id: keyValueId },
            }),
            invalidatesTags: ['KeyValue'],
        }),

        // INTEGRATIONS /////////////////////////////////////////////
        getIntegrations: builder.query<any, { integrationsQuery: any, pagination: PaginationType }>({
            query: ({ integrationsQuery, pagination }) => ({
                url: 'integrations/find',
                method: 'POST',
                body: { integrationsQuery, pagination },
            }),
            providesTags: ['Integration'],
        }),
        getIntegrationById: builder.query<any, string>({
            query: (integrationId) => ({
                url: `integrations/view/${integrationId}`,
                method: 'GET',
            }),
        }),
        addNewIntegration: builder.mutation<any, any>({
            query: (integrationInfo) => ({
                url: 'integrations/add-integration',
                method: 'POST',
                body: { integrationInfo },
            }),
            invalidatesTags: ['Integration'],
        }),
        editIntegration: builder.mutation<any, { integrationId: string, integrationInfo: any }>({
            query: ({ integrationId, integrationInfo }) => ({
                url: 'integrations/edit-integration',
                method: 'POST',
                body: { _id: integrationId, integrationInfo },
            }),
            invalidatesTags: ['Integration'],
        }),
        deleteIntegration: builder.mutation<any, string>({
            query: (integrationId) => ({
                url: 'integrations/delete-integration',
                method: 'POST',
                body: { _id: integrationId },
            }),
            invalidatesTags: ['Integration'],
        }),

        // UPLOAD IMAGE /////////////////////////////////////////////
        uploadImage: builder.mutation<any, any>({
            query: (file: { imageToUpload: any, filename: string }) => ({
                url: `upload/image-upload`,
                method: 'POST',
                body: convertImageToFormData(file),
                headers: {
                    // 'Content-Type': 'multipart/form-data'
                    'Disable-Content-Type': 'yeah'
                },
                formData: true
            }),
        }),

        // EMAIL TEMPLATES /////////////////////////////////////////////
        sendBookingConfirmedEmail: builder.mutation<any, string>({
            query: (bookingId) => ({
                url: `email/send-booking-confirmed-email/${bookingId}`,
                method: 'GET',
            }),
        }),
        sendBookingConfirmedEmailToTarget: builder.mutation<any, any>({
            query: (info: { bookingId: string, emailTarget: string }) => ({
                url: `email/send-booking-confirmed-email-to-target`,
                method: 'POST',
                body: info,
            }),
        }),
        sendRegistrationReminderEmail: builder.mutation<any, string>({
            query: (bookingId) => ({
                url: `email/send-registration-reminder-email/${bookingId}`,
                method: 'GET',
            }),
        }),
        sendRegistrationReminderEmailToTarget: builder.mutation<any, any>({
            query: (info: { bookingId: string, emailTarget: string }) => ({
                url: `email/send-registration-reminder-email-to-target`,
                method: 'POST',
                body: info,
            }),
        }),

        // USER RELATED CALLS /////////////////////////////////////////////
        checkUserCredentials: builder.query<any, void>({
            query: () => ({
                url: 'users/userInfo',
                method: 'GET',
            }),
        }),
        loginUser: builder.mutation<any, any>({
            query: (credentials) => ({
                url: 'users/login',
                method: 'POST',
                body: credentials,
            }),
        }),
        updateProfile: builder.mutation<any, any>({
            query: (updatedProfileInfo) => ({
                url: 'users/update-user',
                method: 'POST',
                body: updatedProfileInfo,
            }),
            invalidatesTags: ['User'],
        }),
        editUser: builder.mutation<any, { userId: string, updatedUserInfo: any }>({
            query: ({ userId, updatedUserInfo }) => ({
                url: 'users/edit-user',
                method: 'POST',
                body: { _id: userId, updatedUserInfo: updatedUserInfo },
            }),
            invalidatesTags: ['User'],
        }),
        changePassword: builder.mutation<any, any>({
            query: (updatedPasswordInfo) => ({
                url: 'users/change-password',
                method: 'POST',
                body: updatedPasswordInfo,
            }),
        }),
        getUsers: builder.query<any, { usersQuery: any, pagination: any }>({
            query: ({ usersQuery, pagination }) => ({
                url: 'users/find',
                method: 'POST',
                body: { usersQuery, pagination },
            }),
            providesTags: ['User'],
        }),
        deleteUser: builder.mutation<any, string>({
            query: (userId) => ({
                url: 'users/delete-user',
                method: 'POST',
                body: { _id: userId },
            }),
            invalidatesTags: ['User'],
        }),
        getUserInfo: builder.query<any, string>({
            query: (userId) => ({
                url: `users/profile/${userId}`,
                method: 'GET',
            }),
            providesTags: ['User'],
        }),
        registerNewUser: builder.mutation<any, any>({
            query: (newUserInfo) => ({
                url: 'users/register',
                method: 'POST',
                body: {
                    user: {
                        email: newUserInfo.email,
                        firstName: newUserInfo.firstName,
                        lastName: newUserInfo.lastName,
                        password: newUserInfo.password,
                        role: newUserInfo.role,
                    },
                },
            }),
            invalidatesTags: ['User'],
        }),
        forgotPassword: builder.mutation<any, string>({
            query: (username) => ({
                url: 'users/forgot-password',
                method: 'POST',
                body: { username },
            }),
        }),
        
        // PROPERTY RECONCILIATION ENDPOINTS /////////////////////////////////////////////
        // icalendar/reconcile-property/:propertyId get request
        reconcilePropertyBookings: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `icalendar/reconcile-property/${propertyId}`,
                method: 'GET',
            }),
            // providesTags: ['PropertyReconciliation'],
        }),

        // FONTAINE ENDPOINTS /////////////////////////////////////////////
        fontaineCheckoutTodayNoCleaning: builder.mutation<any, void>({
            query: () => ({
                url: 'fontaine/checkout-today-no-cleaning',
                method: 'GET',
            }),
        }),
        fontaineCheckinToday: builder.mutation<any, void>({
            query: () => ({
                url: 'fontaine/checkin-today',
                method: 'GET',
            }),
        }),
        fontaineSendCheckinReady: builder.mutation<any, { propertyIdKeys: string[] }>({
            query: (body) => ({
                url: 'fontaine/checkin-today',
                method: 'POST',
                body,
            }),
        }),
        fontaineCleanReady: builder.mutation<any, void>({
            query: () => ({
                url: 'fontaine/clean-ready',
                method: 'GET',
            }),
        }),
        fontaineSendCleanReady: builder.mutation<any, { propertyIdKeys: string[], bypass?: boolean }>({
            query: (body) => ({
                url: 'fontaine/clean-ready',
                method: 'POST',
                body,
            }),
        }),

        // STRIPE PAYMENT INTENTS /////////////////////////////////////////////
        capturePaymentIntent: builder.mutation<any, string>({
            query: (paymentIntentID) => ({
                url: 'stripe/capture-payment-intent',
                method: 'POST',
                body: { paymentIntentID },
            }),
            invalidatesTags: ['Purchase'],
        }),
        cancelPaymentIntent: builder.mutation<any, string>({
            query: (paymentIntentID) => ({
                url: 'stripe/cancel-payment-intent',
                method: 'POST',
                body: { paymentIntentID },
            }),
            invalidatesTags: ['Purchase'],
        }),

        // VRBO INTEGRATION /////////////////////////////////////////////
        vrboUpdateUnit: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/units/update-unit`,
                method: 'POST',
                body: { propertyId },
            }),
            invalidatesTags: ['VRBOProperty'],
        }),
        vrboUpdateUnitRegistration: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/units/update-unit-registration`,
                method: 'POST',
                body: { propertyId },
            }),
            invalidatesTags: ['VRBOProperty'],
        }),
        vrboQueryProperty: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/properties/query-property/`,
                method: 'POST',
                body: { propertyId },
            }),
            invalidatesTags: ['VRBOProperty'],
        }),
        vrboQueryPropertyStatus: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/properties/query-property-status/`,
                method: 'POST',
                body: { propertyId },
            }),
        }),
        vrboQueryPropertyStatusCheckpoints: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/properties/query-property-status-checkpoints/`,
                method: 'POST',
                body: { propertyId },
            }),
        }),
        vrboQueryMissingContent: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/properties/query-missing-content/`,
                method: 'POST',
                body: { propertyId },
            }),
        }),
        vrboQueryPropertyCompliance: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/properties/query-property-compliance/`,
                method: 'POST',
                body: { propertyId },
            }),
        }),
        vrboCreateProperty: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/properties/create-property/`,
                method: 'POST',
                body: { propertyId },
            }),
            invalidatesTags: ['VRBOProperty'],
        }),
        vrboUpdateProperty: builder.mutation<any, { propertyId: string, options?: { general?: boolean, location?: boolean, policies?: boolean, amenities?: boolean } }>({
            query: ({ propertyId, options }) => ({
                url: `vrbo/properties/update-property/`,
                method: 'POST',
                body: { propertyId, options },
            }),
            invalidatesTags: ['VRBOProperty'],
        }),
        vrboSyncPropertyImages: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/property-images/sync-property-images`,
                method: 'POST',
                body: { propertyId },
            }),
        }),
        vrboSyncUnitSpaces: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/unit-spaces/sync-unit-spaces`,
                method: 'POST',
                body: { propertyId },
            }),
        }),
        vrboUpdatePropertyPricing: builder.mutation<string, string>({
            query: (propertyId) => ({
                url: `vrbo/properties/update-property-pricing`,
                method: 'POST',
                body: { propertyId },
                responseHandler: 'text',
            }),
        }),
        vrboUpdatePropertyAvailability: builder.mutation<string, string>({
            query: (propertyId) => ({
                url: `vrbo/properties/update-property-availability`,
                method: 'POST',
                body: { propertyId },
                responseHandler: 'text',
            }),
        }),
        vrboCreateCancellationPolicy: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/cancellation-policies/create-cancellation-policy`,
                method: 'POST',
                body: { propertyId },
            }),
        }),
        vrboReplaceCancellationPolicy: builder.mutation<any, string>({
            query: (cancellationPolicyId) => ({
                url: `vrbo/cancellation-policies/replace-cancellation-policy`,
                method: 'POST',
                body: { cancellationPolicyId },
            }),
        }),
        vrboEnableProperty: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/properties/enable-property`,
                method: 'POST',
                body: { propertyId },
            }),
            invalidatesTags: ['VRBOProperty'],
        }),
        vrboDisableProperty: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/properties/disable-property`,
                method: 'POST',
                body: { propertyId },
            }),
            invalidatesTags: ['VRBOProperty'],
        }),
        vrboQueryMetadata: builder.query<any, string>({
            query: (type) => ({
                url: `vrbo/metadata/query-metadata/${type}`,
            }),
        }),

        // VRBO PROMOTIONS
        vrboQueryPromotionEligibility: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/promotions/promotion-eligibility`,
                method: 'POST',
                body: { propertyId },
            }),
        }),
        vrboQueryPromotions: builder.mutation<any, { propertyId: string, filters?: any, cursor?: string, limit?: number }>({
            query: (body) => ({
                url: `vrbo/promotions/query-promotions`,
                method: 'POST',
                body,
            }),
        }),
        vrboQueryPropertyPromotions: builder.mutation<any, { propertyId: string, filters?: any, cursor?: string, limit?: number }>({
            query: (body) => ({
                url: `vrbo/promotions/query-property-promotions`,
                method: 'POST',
                body,
            }),
        }),
        vrboCreatePromotion: builder.mutation<any, { propertyId: string, promotion: any }>({
            query: (body) => ({
                url: `vrbo/promotions/create-promotion`,
                method: 'POST',
                body,
            }),
        }),
        vrboUpdatePromotion: builder.mutation<any, { propertyId: string, promotionId: string, promotion: any }>({
            query: (body) => ({
                url: `vrbo/promotions/update-promotion`,
                method: 'POST',
                body,
            }),
        }),
        vrboJoinNewListingDiscount: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/promotions/join-new-listing-discount`,
                method: 'POST',
                body: { propertyId },
            }),
        }),
        vrboWithdrawNewListingDiscount: builder.mutation<any, string>({
            query: (propertyId) => ({
                url: `vrbo/promotions/withdraw-new-listing-discount`,
                method: 'POST',
                body: { propertyId },
            }),
        }),

        // VRBO WEBHOOKS
        vrboGetNotificationEventTypes: builder.query<any, void>({
            query: () => ({
                url: `vrbo/webhook-queries/notification-event-types`,
            }),
        }),
        vrboGetNotificationProfile: builder.query<any, void>({
            query: () => ({
                url: `vrbo/webhook-queries/notification-profile`,
            }),
            providesTags: ['VRBOWebhook'],
        }),
        vrboRefreshNotificationProfile: builder.mutation<any, void>({
            query: () => ({
                url: `vrbo/webhook-queries/notification-profile`,
                method: 'GET',
                params: { refresh: true },
            }),
            invalidatesTags: ['VRBOWebhook'],
        }),
        vrboCreateCallbackConfig: builder.mutation<any, { requestTimeoutSeconds?: number }>({
            query: (body) => ({
                url: `vrbo/webhook-queries/create-callback-config`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['VRBOWebhook'],
        }),
        vrboUpdateCallbackConfig: builder.mutation<any, { callbackConfigId: string, callbackUrl?: string, contactEmail?: string, requestTimeoutSeconds?: number }>({
            query: (body) => ({
                url: `vrbo/webhook-queries/update-callback-config`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['VRBOWebhook'],
        }),
        vrboDeleteCallbackConfig: builder.mutation<any, string>({
            query: (callbackConfigId) => ({
                url: `vrbo/webhook-queries/delete-callback-config`,
                method: 'POST',
                body: { callbackConfigId },
            }),
            invalidatesTags: ['VRBOWebhook'],
        }),
        vrboRefreshCallbackConfigSecret: builder.mutation<any, { callbackConfigId: string, keepExistingSecretActive?: boolean }>({
            query: (body) => ({
                url: `vrbo/webhook-queries/refresh-callback-config-secret`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['VRBOWebhook'],
        }),
        vrboSubscribeEventType: builder.mutation<any, { eventType: string, callbackConfigId?: string }>({
            query: (body) => ({
                url: `vrbo/webhook-queries/subscribe-event-type`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['VRBOWebhook'],
        }),
        vrboUnsubscribeEventType: builder.mutation<any, string>({
            query: (eventType) => ({
                url: `vrbo/webhook-queries/unsubscribe-event-type`,
                method: 'POST',
                body: { eventType },
            }),
            invalidatesTags: ['VRBOWebhook'],
        }),
        vrboSendTestNotification: builder.mutation<any, { eventType: string, payload: any }>({
            query: (body) => ({
                url: `vrbo/webhook-queries/send-test-notification`,
                method: 'POST',
                body,
            }),
        }),

        // MIGRATIONS
        updateEarnings: builder.mutation<any, { batchSize: number, lastId: string | null }>({
            query: ({ batchSize, lastId }) => ({
                url: `migrations/update-earnings`,
                method: 'GET',
                params: { batchSize, lastId: lastId || undefined }
            }),
        }),

        updateExtendStayEarnings: builder.mutation<any, { batchSize: number, lastId: string | null }>({
            query: ({ batchSize, lastId }) => ({
                url: `migrations/update-extend-earnings`,
                method: 'GET',
                params: { batchSize, lastId: lastId || undefined }
            }),
        }),

    }),

})

interface PaginationType {
    page: number,
    limit: number,
    sort?: any
    all?: boolean
}

const convertImageToFormData = (imageInput: { imageToUpload: any, filename: string }) => {
    const { imageToUpload, filename } = imageInput
    // fileData.lastModifiedDate = new Date()
    // fileData.name = filename
    const formData = new FormData()
    formData.append('image', imageToUpload, `customFolder/${filename}`)
    // formData.append('image', fileData, filename)
    // console.log('formData', formData)
    // console.log(formData.get('image'))
    return formData
}

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    // PROPERTIES /////////////////////////////////////////////
    useGetPropertiesListQuery,
    useLazyGetPropertiesListQuery,
    useAddNewPropertyMutation,
    useEditPropertyMutation,
    useDeletePropertyMutation,
    useImportPropertiesMutation,
    useBulkEditPropertiesMutation,
    useGetPropertyByIdQuery,
    useLazyGetPropertyByIdQuery,
    useHandleDoorLockStateMutation,

    // BOOKINGS /////////////////////////////////////////////
    useGetBookingsQuery,
    useLazyGetBookingsQuery,
    useAddNewBookingMutation,
    useEditBookingMutation,
    useDeleteBookingMutation,
    useImportBookingsMutation,
    useBulkEditBookingsMutation,
    useGetBookingByIdQuery,

    // BOOKING REQUESTS /////////////////////////////////////////////
    useGetBookingRequestsQuery,
    useAddNewBookingRequestMutation,
    useEditBookingRequestMutation,
    useDeleteBookingRequestMutation,
    useImportBookingRequestsMutation,
    useBulkEditBookingRequestsMutation,
    useGetBookingRequestByIdQuery,

    // BOOKING GUESTS /////////////////////////////////////////////
    useGetBookingGuestsQuery,
    useAddNewBookingGuestMutation,
    useEditBookingGuestMutation,
    useDeleteBookingGuestMutation,
    useImportBookingGuestsMutation,
    useBulkEditBookingGuestsMutation,
    useGetBookingGuestByIdQuery,
    useSendBookingGuestEmailMutation,

    // BUILDINGS /////////////////////////////////////////////
    useGetBuildingsQuery,
    useAddNewBuildingMutation,
    useEditBuildingMutation,
    useDeleteBuildingMutation,
    useImportBuildingsMutation,
    useBulkEditBuildingsMutation,
    useGetBuildingByIdQuery,

    // BUKINS /////////////////////////////////////////////
    useGetBukinsQuery,
    useLazyGetBukinsQuery,
    useAddNewBukinMutation,
    useEditBukinMutation,
    useDeleteBukinMutation,
    useImportBukinsMutation,
    useBulkEditBukinsMutation,
    useGetBukinByIdQuery,

    // BUKIBLES /////////////////////////////////////////////
    useGetBukiblesQuery,
    useLazyGetBukiblesQuery,
    useAddNewBukibleMutation,
    useEditBukibleMutation,
    useDeleteBukibleMutation,
    useImportBukiblesMutation,
    useBulkEditBukiblesMutation,
    useGetBukibleByIdQuery,

    // INQUIRIES /////////////////////////////////////////////
    useGetInquiriesQuery,
    useAddNewInquiryMutation,
    useEditInquiryMutation,
    useDeleteInquiryMutation,
    useImportInquiriesMutation,
    useBulkEditInquiriesMutation,
    useGetInquiryByIdQuery,

    // PROPERTY PRICES /////////////////////////////////////////////
    useGetPropertyPricesQuery,
    useAddNewPropertyPriceMutation,
    useEditPropertyPriceMutation,
    useDeletePropertyPriceMutation,
    useImportPropertyPricesMutation,
    useBulkEditPropertyPricesMutation,
    useGetPropertyPriceByIdQuery,
    useLazyGetPropertyPricesQuery,

    // PURCHASES /////////////////////////////////////////////
    useGetPurchasesQuery,
    useAddNewPurchaseMutation,
    useEditPurchaseMutation,
    useDeletePurchaseMutation,
    useImportPurchasesMutation,
    useBulkEditPurchasesMutation,
    useGetPurchaseByIdQuery,

    // PROPERTY TASKS /////////////////////////////////////////////
    useGetPropertyTasksQuery,
    useAddNewPropertyTaskMutation,
    useEditPropertyTaskMutation,
    useDeletePropertyTaskMutation,
    useGetPropertyTaskByIdQuery,

    // BOOKING MESSAGES /////////////////////////////////////////////
    useGetBookingMessagesQuery,
    useReplyToBookingMessageMutation,

    // MEDIA FILES /////////////////////////////////////////////
    useGetMediaFilesQuery,
    useAddNewMediaFileMutation,
    useEditMediaFileMutation,
    useDeleteMediaFileMutation,
    useImportMediaFilesMutation,
    useBulkEditMediaFilesMutation,
    useGetMediaFileByIdQuery,

    // ICAL RELATED CALLS /////////////////////////////////////////////
    useGetPropertyIcalDataQuery,
    useLazyGetPropertyIcalDataQuery,

    // DATALOGS /////////////////////////////////////////////
    useGetDataLogsQuery,
    useAddNewDataLogMutation,
    useEditDataLogMutation,
    useDeleteDataLogMutation,
    useImportDataLogsMutation,
    useBulkEditDataLogsMutation,
    useGetDataLogByIdQuery,

    // NOTIFICATION ENTRY /////////////////////////////////////////////
    useGetNotificationEntriesQuery,
    useAddNewNotificationEntryMutation,
    useEditNotificationEntryMutation,
    useDeleteNotificationEntryMutation,
    useImportNotificationEntriesMutation,
    useBulkEditNotificationEntriesMutation,
    useGetNotificationEntryByIdQuery,

    // SCRAPE ENTRY /////////////////////////////////////////////
    useGetScrapeEntriesQuery,
    useGetScrapeEntryByIdQuery,
    useAddNewScrapeEntryMutation,
    useEditScrapeEntryMutation,
    useDeleteScrapeEntryMutation,

    // KEY VALUE /////////////////////////////////////////////
    useGetKeyValuesQuery,
    useGetKeyValueByIdQuery,
    useAddNewKeyValueMutation,
    useEditKeyValueMutation,
    useDeleteKeyValueMutation,

    // INTEGRATIONS /////////////////////////////////////////////
    useGetIntegrationsQuery,
    useLazyGetIntegrationsQuery,
    useGetIntegrationByIdQuery,
    useAddNewIntegrationMutation,
    useEditIntegrationMutation,
    useDeleteIntegrationMutation,

    // ADDITIONAL SERVICE /////////////////////////////////////////////
    useGetAdditionalServicesQuery,
    useGetAdditionalServiceByIdQuery,
    useAddNewAdditionalServiceMutation,
    useEditAdditionalServiceMutation,
    useDeleteAdditionalServiceMutation,
    useImportAdditionalServicesMutation,
    useBulkEditAdditionalServicesMutation,
    useGetAdditionalServiceDistinctFieldValuesQuery,

    // TURNOVERS /////////////////////////////////////////////
    useGetTurnoversQuery,
    useGetTurnoverByIdQuery,
    useAddNewTurnoverMutation,
    useEditTurnoverMutation,
    useDeleteTurnoverMutation,
    useImportTurnoversMutation,
    useBulkEditTurnoversMutation,

    // AMENITIES /////////////////////////////////////////////
    useGetAmenitiesQuery,
    useGetAmenityByIdQuery,
    useAddNewAmenityMutation,
    useEditAmenityMutation,
    useDeleteAmenityMutation,

    // PROPERTY AMENITIES /////////////////////////////////////////////
    useGetPropertyAmenitiesQuery,
    useLazyGetPropertyAmenitiesQuery,
    useGetPropertyAmenityByIdQuery,
    useAddNewPropertyAmenityMutation,
    useEditPropertyAmenityMutation,
    useDeletePropertyAmenityMutation,
    useGetBuildingAmenitiesQuery,
    useGetBuildingAmenityByIdQuery,
    useAddNewBuildingAmenityMutation,
    useEditBuildingAmenityMutation,
    useDeleteBuildingAmenityMutation,

    // RESOURCE ACCESS /////////////////////////////////////////////
    useGetResourceAccessListQuery,
    useLazyGetResourceAccessListQuery,
    useGetResourceAccessByIdQuery,
    useAddResourceAccessMutation,
    useEditResourceAccessMutation,
    useDeleteResourceAccessMutation,
    useImportResourceAccessMutation,
    useBulkEditResourceAccessMutation,
    useGetResourceAccessDistinctFieldValuesQuery,
    useGetUserResourceAccessMutation,
    useGetAllUserResourceAccessQuery,
    useGetAllResourceUsersMutation,
    
    useGetUserResourceAccessPropertiesQuery,

    // UPLOAD IMAGE /////////////////////////////////////////////
    useUploadImageMutation,

    // EMAIL TEMPLATES /////////////////////////////////////////////
    useSendBookingConfirmedEmailMutation,
    useSendBookingConfirmedEmailToTargetMutation,
    useSendRegistrationReminderEmailMutation,
    useSendRegistrationReminderEmailToTargetMutation,

    // USER RELATED CALLS /////////////////////////////////////////////
    useCheckUserCredentialsQuery,
    useLazyCheckUserCredentialsQuery,
    useLoginUserMutation,
    useUpdateProfileMutation,
    useEditUserMutation,
    useChangePasswordMutation,
    useGetUsersQuery,
    useDeleteUserMutation,
    useGetUserInfoQuery,
    useRegisterNewUserMutation,
    useForgotPasswordMutation,

    // PROPERTY RECONCILIATION ENDPOINTS /////////////////////////////////////////////

    useReconcilePropertyBookingsMutation,

    // FONTAINE ENDPOINTS /////////////////////////////////////////////
    useFontaineCheckoutTodayNoCleaningMutation,
    useFontaineCheckinTodayMutation,
    useFontaineSendCheckinReadyMutation,
    useFontaineCleanReadyMutation,
    useFontaineSendCleanReadyMutation,

    // STRIPE PAYMENT INTENTS /////////////////////////////////////////////
    useCapturePaymentIntentMutation,
    useCancelPaymentIntentMutation,

    // VRBO INTEGRATION /////////////////////////////////////////////
    useVrboUpdateUnitMutation,
    useVrboUpdateUnitRegistrationMutation,
    useVrboQueryPropertyMutation,
    useVrboQueryPropertyStatusMutation,
    useVrboQueryPropertyStatusCheckpointsMutation,
    useVrboQueryMissingContentMutation,
    useVrboQueryPropertyComplianceMutation,
    useVrboCreatePropertyMutation,
    useVrboUpdatePropertyMutation,
    useVrboSyncPropertyImagesMutation,
    useVrboSyncUnitSpacesMutation,
    useVrboUpdatePropertyPricingMutation,
    useVrboUpdatePropertyAvailabilityMutation,
    useVrboCreateCancellationPolicyMutation,
    useVrboReplaceCancellationPolicyMutation,
    useVrboEnablePropertyMutation,
    useVrboDisablePropertyMutation,
    useVrboQueryMetadataQuery,
    useLazyVrboQueryMetadataQuery,
    useVrboQueryPromotionEligibilityMutation,
    useVrboQueryPromotionsMutation,
    useVrboQueryPropertyPromotionsMutation,
    useVrboCreatePromotionMutation,
    useVrboUpdatePromotionMutation,
    useVrboJoinNewListingDiscountMutation,
    useVrboWithdrawNewListingDiscountMutation,
    useVrboGetNotificationEventTypesQuery,
    useVrboGetNotificationProfileQuery,
    useVrboRefreshNotificationProfileMutation,
    useVrboCreateCallbackConfigMutation,
    useVrboUpdateCallbackConfigMutation,
    useVrboDeleteCallbackConfigMutation,
    useVrboRefreshCallbackConfigSecretMutation,
    useVrboSubscribeEventTypeMutation,
    useVrboUnsubscribeEventTypeMutation,
    useVrboSendTestNotificationMutation,

    // MIGRATIONS /////////////////////////////////////////////
    useUpdateEarningsMutation,
    useUpdateExtendStayEarningsMutation,

} = bClientApi


// https://stackoverflow.com/questions/70648692/the-right-way-to-use-conditional-header-in-rtk-query
